import {
  HStack,
  OverlayModal,
  OverlayModalHeaderProps,
  parsingPhoneNumber,
  colors,
  getCdnImageSrc,
  GrayNonBorderDropdown,
  HLine,
  Image,
  LargeButton,
  LARGEL_BUTTON_TYPE,
  MAYFLOWER_ADDITIONAL_FEE,
  Spacing,
  VStack,
  getSeoulTime,
} from "goi_common";
import { Box, Input, Radio, RadioGroup } from "@chakra-ui/react";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";

import { FlowerOrderRequestBody } from "@/apis/wreath";
import getPostPosition from "@/utils/getPostPosition";

import { usePostWreathOrderMutation } from "@/hooks/usePostWreathOrderMutation";
import PARLOR_DETAIL from "@/constants/parlors_detail.json";

interface FormState {
  price: string;
  name: string;
  sangju: string;
  phone: string;
  address: string;
  detailAddress: string;
  leftSentence: string;
  rightSentence: string;
  email: string;
  requirements?: string;
}

interface FormUIState {
  errorText: string;
}

type InputState = FormState & FormUIState;

const 오른쪽리본문구들: { [key: string]: string }[] = [
  { key: "삼가 故人의 冥福을 빕니다", value: "삼가 故人의 冥福을 빕니다" },
  { key: "삼가 고인의 명복을 빕니다", value: "삼가 고인의 명복을 빕니다" },
  { key: "謹弔", value: "謹弔" },
  { key: "근조", value: "근조" },
];

const 화환제출문구: { [key: string]: string } = {
  price: "근조화환",
  sangju: "고인 성함",
  address: "배송지",
  detailAddress: "상세주소",
  rightSentence: "오른쪽 리본 문구",
  leftSentence: "왼쪽 리본 문구",
  name: "신청자 성함",
  phone: "신청자 휴대전화",
  email: "주문자 이메일",
};

export const 메이플라워_문의전화 = "1666-5522";
export const 메이플라워_화환표 = [
  {
    id: 4,
    image: "bugo/flower/3_yellow.jpg",
    alt: "근조3단(기본형)",
    name: "근조3단(기본형)",
    price: "11만원",
    discountPrice: "99,000원",
  },
  // {
  //   id: 5,
  //   image: "bugo/flower/3_red.jpg",
  //   alt: "근조3단(기본형)",
  //   name: "근조3단(기본형)",
  //   price: "11만원",
  //   discountPrice: "99,000원",
  // },
  {
    id: 3,
    image: "bugo/flower/3_luxury.jpg",
    alt: "근조화환(고급형)",
    name: "근조화환(고급형)",
    price: "13만원",
    discountPrice: "119,000원",
  },
  {
    id: 2,
    image: "bugo/flower/3_large.jpg",
    alt: "근조화환(특대형)",
    name: "근조화환(특대형)",
    price: "16만원",
    discountPrice: "149,000원",
  },
  {
    id: 1,
    image: "bugo/flower/4_flower.jpg",
    alt: "근조4단",
    name: "근조4단",
    price: "19만원",
    discountPrice: "179,000원",
  },
  {
    id: 6,
    image: "bugo/flower/basket.jpg",
    alt: "근조바구니",
    name: "근조바구니",
    price: "9만원",
    discountPrice: "79,000원",
  },
];

const DEFAULT_FLOWER_ITEM = 메이플라워_화환표[0];

const selectedReducer = (
  prev: InputState,
  next: { [key: string]: string | boolean }
) => {
  const newState = { ...prev, ...next };

  const optionKeys = ["requirements", "errorText"];

  const emptyRequiredValues = Object.entries(newState).filter(
    ([key, value]) => !optionKeys.includes(key) && !value
  );

  switch (emptyRequiredValues.length) {
    /** 모든 항목이 입력된 경우 */
    case 0:
      return {
        ...newState,
        errorText: "",
      };

    case 1:
      const itemKey = emptyRequiredValues[0][0];
      const itemTitle = 화환제출문구[itemKey];
      const message = " 입력해 주세요.";

      return {
        ...newState,
        errorText: `${
          itemTitle + getPostPosition(itemTitle, true) + `${message}`
        }`,
      };
    default:
      return {
        ...newState,
        errorText: "모든 항목을 입력해 주세요.",
      };
  }
};

export default function MissParlorDetailBadgeCTASection({
  companyName,
  parlorId,
  onClose,
  isDiscount = true,
}: {
  companyName: string;
  parlorId: number;
  onClose: () => void;
  isDiscount?: boolean;
}) {
  const [selectedFlowerId, setSelectedFlowerId] = useState<string>(
    DEFAULT_FLOWER_ITEM.id.toString()
  );
  const selectedFlower = 메이플라워_화환표.find(
    (item) => item.id === Number(selectedFlowerId)
  );

  const [selected, setSelected] = useReducer(selectedReducer, {
    price: isDiscount
      ? DEFAULT_FLOWER_ITEM.discountPrice
      : DEFAULT_FLOWER_ITEM.price,
    name: "",
    sangju: "",
    phone: "",
    address: companyName,
    detailAddress: "",
    leftSentence: "",
    rightSentence: "",
    email: "",
    requirements: "",
    errorText: "모든 항목을 입력해 주세요.",
  });

  useEffect(() => {
    if (selectedFlower) {
      setSelected({
        price: isDiscount
          ? selectedFlower?.discountPrice
          : selectedFlower?.price,
      });
    }
  }, [selectedFlower]);

  const { mutate: sendWreath, isLoading } = usePostWreathOrderMutation({
    onClose,
  });

  const postRequest = (totalAmount: number) => {
    if (!/^010-[2-9]{1}\d{3}-\d{4}$/.test(selected.phone)) {
      const alertText = "번호 형식을 체크해주세요. (010-****-****)";
      return alert(alertText);
    }

    if (selectedFlower) {
      const body: FlowerOrderRequestBody = {
        item_name: selectedFlower.name,
        total_amount: totalAmount,
        item_thumbnail_url: getCdnImageSrc(selectedFlower.image || ""),
        recipient_name: selected.sangju,
        left_sentence: selected.leftSentence,
        right_sentence: selected.rightSentence,
        email: selected.email,
        name: selected.name,
        phone: selected.phone,
        base_address: selected.address,
        address_detail: selected.detailAddress,

        requirements: selected.requirements,
        parlor: parlorId?.toString(),
      };

      sendWreath(body);
    }
  };

  const isAfterDuty = useMemo(() => {
    const seoulHour = getSeoulTime().hour();
    return seoulHour > 15 || seoulHour < 8;
  }, []);

  const onChangeInput = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;

      if (name === "phone") {
        setSelected({ [name]: parsingPhoneNumber(value) });
        return;
      }

      setSelected({ [name]: value });
    },
    [setSelected]
  );

  const { 최종결제금액, flowerShippingFee } = useGetFlowerShippingFee(
    parlorId,
    selectedFlowerId
  );

  return (
    <VStack padding="20px">
      <VStack
        className="body_14_m orange650"
        padding="13px 20px"
        background={colors.warmGray50}
      >
        주문 후 3시간 이내 발송되며,
        <br />
        <div>
          <b>오후 6시 이후 주문은 익일 오전</b>에 발송됩니다.
        </div>
        배송 완료 후, 입력하신 번호로 확인 사진을 보내드립니다.
        <br />
      </VStack>

      <Spacing gap={20} />

      <RadioGroup
        margin="0 0 30px 0"
        value={selectedFlowerId}
        onChange={(e) => {
          setSelectedFlowerId(e);
        }}
      >
        <div
          css={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "10px",
          }}
        >
          {메이플라워_화환표.map((item) => {
            return (
              <div
                key={item.id}
                css={{
                  cursor: "pointer",
                  borderRadius: "10px",
                  padding: "7px 3px",
                  border:
                    selectedFlowerId === item.id.toString()
                      ? `2px solid ${colors.orange650}`
                      : undefined,
                }}
                onClick={() => {
                  setSelectedFlowerId(item.id.toString());
                }}
              >
                <FlowerSelectItem item={item} isDiscount={isDiscount} />
              </div>
            );
          })}
        </div>
      </RadioGroup>

      {flowerShippingFee ? (
        <>
          <VStack
            padding="18px 16px 22px 16px"
            background={colors.gray50}
            borderRadius="10px"
          >
            <VStack gap={"8px"}>
              <HStack>
                <div
                  className="body_16_m gray500"
                  css={{
                    minWidth: "100px",
                  }}
                >
                  판매가격
                </div>
                <div className="subtitle_16_sb gray700">
                  {selectedFlower?.discountPrice}
                </div>
              </HStack>
              <HStack>
                <div
                  className="body_16_m gray500"
                  css={{
                    minWidth: "100px",
                  }}
                >
                  배송비
                </div>
                <div className="subtitle_16_sb gray700">
                  {`${flowerShippingFee.toLocaleString()}원`}
                </div>
              </HStack>
            </VStack>
            <HLine color={colors.gray300} margin="10px 0" />
            <HStack>
              <div
                className="body_16_m gray500"
                css={{
                  minWidth: "100px",
                }}
              >
                최종 결제금액
              </div>
              <div className="subtitle_16_sb gray700">{최종결제금액}</div>
            </HStack>
          </VStack>
          <Spacing gap={30} />
        </>
      ) : null}

      <MissFlowerDeliveryPolicyButton />

      <VStack gap={12}>
        <InputComponent
          title="고인 성함"
          name="sangju"
          placeholder="예) 김인호"
          value={selected.sangju}
          onChange={onChangeInput}
        />
        <InputComponent
          title="배송지"
          name="address"
          placeholder="예) 밀양농협장례식장, 쉴낙원 남대전 장례식장"
          value={selected.address}
          onChange={onChangeInput}
          readOnly={true}
        />
        <InputComponent
          title="상세주소"
          name="detailAddress"
          placeholder="예) 101호"
          value={selected.detailAddress}
          onChange={onChangeInput}
        />
        <VStack>
          <Box className="body_16_m">오른쪽 리본 문구</Box>
          <GrayNonBorderDropdown
            name="문구를 선택해주세요."
            values={오른쪽리본문구들}
            value={selected.rightSentence}
            onChange={(e: ChangeEvent<HTMLSelectElement>) => {
              setSelected({ rightSentence: e.target.value });
            }}
          />
        </VStack>
        <InputComponent
          title="왼쪽 리본 문구"
          name="leftSentence"
          placeholder="예) 고이 대표이사 송슬옹, OO고등학교 19기 일동"
          value={selected.leftSentence}
          onChange={onChangeInput}
        />
        <InputComponent
          title="신청자 성함"
          name="name"
          placeholder="예) 송슬옹"
          value={selected.name}
          onChange={onChangeInput}
        />

        <InputComponent
          title="신청자 휴대전화"
          name="phone"
          placeholder="예) 010-0000-0000"
          value={selected.phone}
          onChange={onChangeInput}
        />
      </VStack>

      <VStack margin="21px 0 0 0" gap={16}>
        <InputComponent
          title="주문자 이메일"
          name="email"
          placeholder="예) abc123@email.com"
          description="*해당 이메일로 영수증이 발급됩니다."
          value={selected.email || ""}
          onChange={onChangeInput}
        />

        <InputComponent
          title="[선택] 배송 요청사항"
          name="requirements"
          placeholder="예) 빠른 배송 부탁합니다."
          value={selected.requirements || ""}
          onChange={onChangeInput}
        />
      </VStack>

      <VStack gap={8}>
        <Box className="body_14_m orange650 font-center" padding="12px">
          {isAfterDuty && (
            <div className="danger">
              오후 6시 이후에는 제휴 꽃집 영업이 종료되어
              <br />
              익일 9시 이후 순차 배송됩니다.
              <br />
            </div>
          )}
        </Box>

        <Box
          className="body_14_m"
          padding="10px"
          background={colors.warmGray50}
        >
          문의 전화 : <b>{메이플라워_문의전화}</b>
        </Box>
      </VStack>

      <Spacing gap={20} />
      <b className="orange650 body_14_m">{selected.errorText}</b>
      <Spacing gap={60} />

      <VStack position="absolute" css={{ bottom: 0, left: 0 }} width="100%">
        <LargeButton
          buttonType={
            isLoading || !!selected.errorText
              ? LARGEL_BUTTON_TYPE.DISABLED
              : LARGEL_BUTTON_TYPE.ORANGE
          }
          disabled={isLoading || !!selected.errorText || !selectedFlower}
          onClick={() => {
            최종결제금액 &&
              postRequest(parseInt(최종결제금액.replaceAll(/[^0-9]/g, "")));
          }}
        >
          {isLoading ? "화환 발송 요청중..." : "화환 발송 요청하기"}
        </LargeButton>
      </VStack>
    </VStack>
  );
}

const InputComponent = ({
  title,
  name,
  placeholder,
  value,
  description,
  onChange,
  readOnly = false,
}: {
  title: string;
  name: string;
  placeholder: string;
  value: string;
  description?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
}) => {
  return (
    <VStack>
      <Box className="body_16_m">{title}</Box>
      <Input
        className="body_14_m"
        size="sm"
        name={name}
        border={`1px solid ${colors.gray300}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        readOnly={readOnly}
      />
      {description && <Box className="danger subtitle_14_b">{description}</Box>}
    </VStack>
  );
};

function FlowerModalContentHeader({ onClose }: { onClose: () => void }) {
  const headerProps: OverlayModalHeaderProps = {
    item: {
      title: `근조화환 빠른주문`,
      onClick: () => {
        onClose();
      },
      closeEvent: { newGtm: { path: "home", trigger_and_target: "" } },
    },
  };
  return <OverlayModal.Header.CloseBtnType props={headerProps} />;
}

MissParlorDetailBadgeCTASection.header = FlowerModalContentHeader;

const FlowerSelectItem = ({
  item,
  isDiscount,
}: {
  item: typeof 메이플라워_화환표[number];
  isDiscount: boolean;
}) => {
  return (
    <VStack className="body_14_m" alignItems="center" gap={6} key={item.id}>
      <VStack alignItems="center">
        <div>{item.name}</div>
        <div>({item.price})</div>
      </VStack>
      {isDiscount && (
        <>
          <Box marginTop="-18px">
            <HLine height="4px" width="50px" color="#ff210099" />
          </Box>
          <Box className="subtitle_16_b">{item.discountPrice}</Box>
        </>
      )}
      <Image src={getCdnImageSrc(item.image)} width="110px" alt={item.alt} />
      <Radio
        border={`1px solid ${colors.gray300}`}
        colorScheme={"orange"}
        value={item.id.toString()}
      />
    </VStack>
  );
};

const MissFlowerDeliveryPolicyButton = () => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <VStack
      className="body_14_m"
      gap={12}
      alignItems="center"
      css={{
        marginTop: "20px",
        marginBottom: "40px",
      }}
    >
      <div>배송/교환/환불에 대한 자세한 내용을 확인해주세요.</div>
      <button
        className="orange700 button_14_sb"
        css={{
          background: colors.orange100,
          borderRadius: "15px",
          padding: "10px 14px",
          width: "77px",
        }}
        onClick={() => setIsOpen(true)}
      >
        확인하기
      </button>
      {isOpen && (
        <OverlayModal
          closeOnOverlayClick
          overlayModalContent={<MissFlowerDeliveryPolicy />}
          isOpen={isOpen}
          modalContentStyle={{
            minWidth: "320px",
            height: "390px",
            minHeight: "390px",
            borderRadius: "20px",
          }}
          onClose={() => setIsOpen(false)}
        />
      )}
    </VStack>
  );
};

const MissFlowerDeliveryPolicy = () => {
  return (
    <VStack className="body_16_r" padding="20px" gap="40px">
      <VStack>
        {/* <h4 className="subtitle_18_sb">배송정보</h4> */}
        <ul css={{ listStyle: "disc", margin: "0px 0px 0px 16px" }}>
          <li>
            구입금액과 지역 관계 없이 무료 배송
            <br />
            (단, 산간지역과 섬지역은 예외) 가능
          </li>
          <li>배송시간 : 09시부터 20시까지</li>
          <li>배송소요시간 : 3시간 이내 배송</li>
        </ul>
      </VStack>
      <VStack>
        <h3 className="subtitle_18_sb">교환/환불</h3>
        <span>
          교환과 환불은 주문한 상품에 대한 아래와 같은 요소 발생시에 100%
          가능합니다.
        </span>
      </VStack>
      <VStack>
        <h4 className="subtitle_18_sb">교환요인</h4>
        <ol css={{ margin: "0px 0px 0px 16px" }}>
          <li>배송된 상품이 파손됐거나 오염됐을 경우</li>
          <li> 배송된 상품이 주문한 내용과 다를 경우</li>
          <li> 쇼핑몰이 제공한 정보와 다를 경우</li>
        </ol>
      </VStack>
      <VStack>
        <h4 className="subtitle_18_sb">환불요인</h4>
        <ol css={{ margin: "0px 0px 0px 16px" }}>
          <li>배송시간 3시간 전에 주문이 취소되었을 경우</li>
          <li>상품이 품절됐거나 기타 사유로 인해 배송이 불가능한 이유</li>
        </ol>
      </VStack>
      <div>
        ※ 환불 처리는 위와 같은 요인이 발생 시 12시간 이내에 환불처리
        해드립니다.
        <br />
        <div
          className="subtitle_16_b gray800"
          css={{
            paddingTop: "12px",
          }}
        >
          ※ 화환 제작 후 취소 시 일부 금액만 환불될 수 있음을 미리 알려드립니다.
        </div>
      </div>
    </VStack>
  );
};

export const useGetFlowerShippingFee = (parlorId: number, flowerId: string) => {
  const [isInitializing, setIsInitializing] = useState(true);

  // 화환 정보 조회
  const flower = useMemo(
    () => 메이플라워_화환표.find(({ id }) => id === Number(flowerId)),
    [flowerId]
  );

  // 장례식장 상세 정보
  const parlorDetail = PARLOR_DETAIL.find(({ id }) => id === parlorId);

  // 배송비 계산
  const flowerShippingFee = useMemo(() => {
    if (!parlorDetail) return null;

    // 특정 지역 장례식장 고정 배송비 설정
    const FIXED_FEE_PARLORS = {
      영주시_풍기읍: [753, 910],
      익산시_함열읍: [374, 375],
      창원시_진해구_용원로: [72, 306],
      서안동: [1041],
    };

    if (
      [
        ...FIXED_FEE_PARLORS.영주시_풍기읍,
        ...FIXED_FEE_PARLORS.익산시_함열읍,
        ...FIXED_FEE_PARLORS.창원시_진해구_용원로,
        ...FIXED_FEE_PARLORS.서안동,
      ].includes(parlorDetail.id)
    ) {
      return 10000;
    }

    return parlorDetail.sigungu
      ? MAYFLOWER_ADDITIONAL_FEE[parlorDetail.sigungu]?.additionalFee
      : null;
  }, [parlorDetail]);

  // 최종 결제 금액 계산
  const 최종결제금액 = useMemo(() => {
    if (!flower || !parlorDetail) return null;

    const basePrice = parseInt(flower.discountPrice.replace(/,/g, ""));
    const totalPrice = flowerShippingFee
      ? basePrice + flowerShippingFee
      : basePrice;

    setIsInitializing(false);

    return `${totalPrice.toLocaleString()}원`;
  }, [flower, flowerShippingFee, parlorDetail]);

  return { isInitializing, flowerShippingFee, 최종결제금액 };
};
